import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventType } from '@azure/msal-browser';
import { Observable, ReplaySubject, Subscription, from, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { environment } from '../../../environments/environment';

import { BackendThemeData } from '../interfaces/theme.interface';
import { FlatThemeCustomization } from '../interfaces/flat-theme.interface';
import { FlatThemeService } from './flat-theme.service';
import { ThemeBuilderService } from './theme-builder.service';
import { ThemeService } from './theme.service';
import { FiscalType } from 'src/app/pages/my-account/components/company-settings/company-settings.component';
import { CurrencyCode } from 'src/app/core/utils/currency.utils';

export interface CenterGroupStyle {
    hueColor?: number;
    image?: string;
    themePrimary?: string;
    themePrimaryHover?: string;
    themeSecondary?: string;
    themeTextPrimary?: string;
    themeBorder?: string;
    themeTopbarBg?: string;
    themeProfileCardBg?: string;
    themeArrowButtonBg?: string;
    themeArrowButtonHoverBg?: string;
    themeArrowButtonIconColor?: string;
    themeSidebarActiveBg?: string;
    themePendingOrdersBg?: string;
    themePendingOrdersBorder?: string;
    themeTodayBg?: string;
    themeLinkColor?: string;
    themeClientCountBg?: string;
    themeClientCountText?: string;
    themeLogoUrl?: string;
    themeLogoWhiteUrl?: string;
    themeLogoSmallUrl?: string;
    themeFaviconUrl?: string;
    themeUserAvatarUrl?: string;
    themeChevronImageUrl?: string;
    themeTopbarLogoUrl?: string;
}

export interface User {
    id: number;
    fullName: string;
    email: string;
    isActive: boolean;
    roles: string[];
    centerId: number;
    country: string;
    centerGroupId: number | null;
    groupCenterStyle?: CenterGroupStyle;
    centerGroupStyle?: CenterGroupStyle;
    brandingData?: BackendThemeData;
    currency: CurrencyCode;
    fiscalType: FiscalType;
}

type NormalizedRole = 'none' | string;

@Injectable({ providedIn: 'root' })
export class UserRoleService implements OnDestroy {
    private readonly apiUrl = environment.cafler_backoffice_back_url;

    private currentUserRequest$: Observable<User | null> | null = null;

    private readonly userSubject = new ReplaySubject<User | null>(1);
    private readonly roleSubject = new ReplaySubject<NormalizedRole>(1);
    private readonly centerHueSubject = new ReplaySubject<number | null>(1);
    private readonly centerLogoSubject = new ReplaySubject<string | null>(1);
    private readonly centerCountrySubject = new ReplaySubject<string | null>(1);

    readonly currentUser$: Observable<User | null> = this.userSubject.asObservable().pipe(distinctUntilChanged());
    readonly userRole$: Observable<NormalizedRole> = this.roleSubject.asObservable().pipe(distinctUntilChanged());
    readonly centerHue$: Observable<number | null> = this.centerHueSubject.asObservable().pipe(distinctUntilChanged());
    readonly centerLogo$: Observable<string | null> = this.centerLogoSubject.asObservable().pipe(distinctUntilChanged());
    readonly centerCountry$: Observable<string | null> = this.centerCountrySubject.asObservable().pipe(distinctUntilChanged());

    private readonly rolePriority: NormalizedRole[] = [
        'superadmin',
        'groupadmin',
        'centeradmin',
        'centeruser'
    ];

    private readonly subs = new Subscription();

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private readonly msalService: MsalService,
        private readonly themeService: ThemeService,
        private readonly themeBuilder: ThemeBuilderService,
        private readonly flatThemeService: FlatThemeService
    ) {
        this.userSubject.next(null);
        this.roleSubject.next('none');
        this.centerCountrySubject.next(null);
        this.centerHueSubject.next(145);
        this.centerLogoSubject.next(null);
        this.attachMsalEventCallback();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    getCurrentUser(): Observable<User | null> {
        if (this.currentUserRequest$) return this.currentUserRequest$;

        const account =
            this.msalService.instance.getActiveAccount() ||
            this.msalService.instance.getAllAccounts()[0];
        if (!account) {
            this.setUserState(null);
            this.applyDefaultTheme();
            return of(null);
        }

        this.currentUserRequest$ = this.fetchCurrentUser().pipe(
            tap((user) => this.setUserState(user)),
            catchError((error) => {
                console.error('[UserRoleService] Error loading user', error);
                this.setUserState(null);
                this.applyDefaultTheme();
                return of(null);
            }),
            shareReplay(1)
        );

        return this.currentUserRequest$;
    }

    refreshCurrentUser(): Observable<User | null> {
        this.currentUserRequest$ = null;
        return this.getCurrentUser();
    }

    getUserRole(): Observable<string> {
        return this.userRole$;
    }

    getCenterHue(): Observable<number | null> {
        return this.centerHue$;
    }

    getCenterLogo(): Observable<string | null> {
        return this.centerLogo$;
    }

    hasRole(roles: string | string[]): Observable<boolean> {
        const expected = (Array.isArray(roles) ? roles : [roles]).map(this.normalizeRole);

        return this.getCurrentUser().pipe(
            map((user) => {
                const primaryRole = this.getHighestRole(user);
                this.roleSubject.next(primaryRole);
                const normalizedRoles = this.getNormalizedRoles(user);
                return normalizedRoles.some((role) => role !== 'none' && expected.includes(role));
            }),
            catchError((error) => {
                console.error('[UserRoleService] Error checking role', error);
                return of(false);
            })
        );
    }

    private attachMsalEventCallback(): void {
        const callbackId = this.msalService.instance.addEventCallback((message) => {
            if (!message) return;
            const type = message.eventType;
            if (type === EventType.LOGIN_SUCCESS) {
                const payload = message.payload as AuthenticationResult | null;
                if (payload?.account) {
                    this.msalService.instance.setActiveAccount(payload.account);
                }
                this.refreshCurrentUser().subscribe();
                return;
            }

            if (type === EventType.ACQUIRE_TOKEN_SUCCESS) {
                if (!this.currentUserRequest$) {
                    this.refreshCurrentUser().subscribe();
                }
            }
        });

        if (callbackId) {
            this.subs.add(() => {
                try {
                    this.msalService.instance.removeEventCallback(callbackId);
                } catch {
                    // ignore
                }
            });
        }
    }

    private fetchCurrentUser(): Observable<User> {
        return this.getAuthHeaders().pipe(
            switchMap((headers) => this.http.get<User>(`${this.apiUrl}/users/current`, { headers }))
        );
    }

    private getAuthHeaders(): Observable<HttpHeaders> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) return throwError(() => new Error('No token available'));
                return of(new HttpHeaders({ Authorization: `Bearer ${token}` }));
            })
        );
    }

    private setUserState(user: User | null): void {
        this.userSubject.next(user);

        const role = this.getHighestRole(user);
        this.roleSubject.next(role);

        this.centerCountrySubject.next(user?.country ?? null);

        this.applyUserTheme(user);
        if (!user) {
            this.applyDefaultTheme();
        }
        this.applyLegacyThemeCompatibility(user);
    }

    clearUserState(): void {
        this.currentUserRequest$ = null;
        this.setUserState(null);
    }

    getHighestRole(user: User | null): NormalizedRole {
        const roles = this.getNormalizedRoles(user);
        for (const role of this.rolePriority) {
            if (roles.includes(role)) return role;
        }
        return roles[0] ?? 'none';
    }

    getNormalizedRoles(user: User | null): NormalizedRole[] {
        if (!user?.roles?.length) return ['none'];
        return user.roles.map(this.normalizeRole);
    }

    private normalizeRole(role: string): NormalizedRole {
        return role.trim().toLowerCase();
    }


    private applyUserTheme(user: User | null): void {
        if (!user) return;

        try {
            const backendTheme = this.extractBackendThemeData(user);

            const flatTheme = this.buildFlatTheme(user);
            if (flatTheme && this.flatThemeService.applyFlatTheme(flatTheme)) {
                return;
            }

            const defaultTheme = this.themeBuilder.getDefaultTheme();
            const themeConfig = this.themeBuilder.buildFromBackendData(backendTheme, defaultTheme);
            this.themeService.applyTheme(themeConfig);
        } catch (error) {
            console.error('[UserRoleService] Error applying theme', error);
            this.applyDefaultTheme();
        }
    }

    private applyDefaultTheme(): void {
        const defaultTheme = this.themeBuilder.getDefaultTheme();
        const themeConfig = this.themeBuilder.buildFromBackendData({}, defaultTheme);
        this.themeService.applyTheme(themeConfig);
    }

    private applyLegacyThemeCompatibility(user: User | null): void {
        const style = user?.centerGroupStyle ?? user?.groupCenterStyle;
        this.centerHueSubject.next(style?.hueColor ?? 145);
        this.centerLogoSubject.next(style?.image ?? null);
    }

    private buildFlatTheme(user: User): FlatThemeCustomization | null {
        const style = user.centerGroupStyle ?? user.groupCenterStyle;
        if (!style) return null;

        const flat: Partial<FlatThemeCustomization> = {};
        let hasAny = false;

        const cssVarMappings: ReadonlyArray<[keyof CenterGroupStyle, keyof FlatThemeCustomization]> = [
            ['themePrimary', 'theme-primary'],
            ['themePrimaryHover', 'theme-primary-hover'],
            ['themeSecondary', 'theme-secondary'],
            ['themeTextPrimary', 'theme-text-primary'],
            ['themeBorder', 'theme-border'],
            ['themeTopbarBg', 'theme-topbar-bg'],
            ['themeProfileCardBg', 'theme-profile-card-bg'],
            ['themeArrowButtonBg', 'theme-arrow-button-bg'],
            ['themeArrowButtonHoverBg', 'theme-arrow-button-hover-bg'],
            ['themeArrowButtonIconColor', 'theme-arrow-button-icon-color'],
            ['themeSidebarActiveBg', 'theme-sidebar-active-bg'],
            ['themePendingOrdersBg', 'theme-pending-orders-bg'],
            ['themePendingOrdersBorder', 'theme-pending-orders-border'],
            ['themeTodayBg', 'theme-today-bg'],
            ['themeLinkColor', 'theme-link-color'],
            ['themeClientCountBg', 'theme-client-count-bg'],
            ['themeClientCountText', 'theme-client-count-text'],
        ];

        cssVarMappings.forEach(([fromKey, toKey]) => {
            const value = style[fromKey];
            if (typeof value === 'string' && value.length > 0) {
                flat[toKey] = value;
                hasAny = true;
            }
        });

        const assetMappings: ReadonlyArray<[keyof CenterGroupStyle, keyof FlatThemeCustomization]> = [
            ['themeUserAvatarUrl', 'userAvatarUrl'],
            ['themeChevronImageUrl', 'chevronImageUrl'],
            ['themeTopbarLogoUrl', 'logoUrl'],
        ];

        assetMappings.forEach(([fromKey, toKey]) => {
            const value = style[fromKey];
            if (typeof value === 'string' && value.length > 0) {
                flat[toKey] = value;
                hasAny = true;
            }
        });

        return hasAny ? (flat as FlatThemeCustomization) : null;
    }

    private extractBackendThemeData(user: User): BackendThemeData {
        if (user.brandingData) return user.brandingData;

        const style = user.centerGroupStyle ?? user.groupCenterStyle;
        if (!style) return {};

        const theme: BackendThemeData = {
            brandName: String(user.fullName ?? '').trim(),
            brandId: String(user.centerId),
            isActive: true,
            lastUpdated: new Date().toISOString(),

            primaryColor: style.themePrimary,
            secondaryColor: style.themeSecondary,
            textPrimaryColor: style.themeTextPrimary,
            borderColor: style.themeBorder,
            topbarBgColor: style.themeTopbarBg,
            profileCardBgColor: style.themeProfileCardBg,
            arrowButtonBgColor: style.themeArrowButtonBg,
            arrowButtonHoverBgColor: style.themeArrowButtonHoverBg,
            arrowButtonIconColor: style.themeArrowButtonIconColor,
            sidebarActiveBgColor: style.themeSidebarActiveBg,
            pendingOrdersBackgroundColor: style.themePendingOrdersBg,
            pendingOrdersBorder: style.themePendingOrdersBorder,
            todayBackgroundColor: style.themeTodayBg,
            linkColor: style.themeLinkColor,
            clientCountBgColor: style.themeClientCountBg,
            clientCountTextColor: style.themeClientCountText,

            logoUrl: this.resolveLogo(style),
            logoWhiteUrl: style.themeLogoWhiteUrl,
            logoSmallUrl: style.themeLogoSmallUrl,
            faviconUrl: style.themeFaviconUrl,
            userAvatarUrl: style.themeUserAvatarUrl,
            chevronImageUrl: style.themeChevronImageUrl,
            topbarLogoUrl: style.themeTopbarLogoUrl,
        };

        if (!theme.primaryColor && typeof style.hueColor === 'number') {
            theme.primaryColor = this.hueToHex(style.hueColor);
        }

        return theme;
    }

    private resolveLogo(style: CenterGroupStyle): string | undefined {
        if (style.themeTopbarLogoUrl) return style.themeTopbarLogoUrl;
        if (style.themeLogoUrl) return style.themeLogoUrl;
        if (style.image) return style.image;
        return undefined;
    }

    private hueToHex(hue: number): string {
        const saturation = 70;
        const lightness = 50;

        const c = (1 - Math.abs((2 * lightness) / 100 - 1)) * (saturation / 100);
        const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
        const m = lightness / 100 - c / 2;

        let r = 0;
        let g = 0;
        let b = 0;

        if (hue >= 0 && hue < 60) {
            r = c; g = x; b = 0;
        } else if (hue >= 60 && hue < 120) {
            r = x; g = c; b = 0;
        } else if (hue >= 120 && hue < 180) {
            r = 0; g = c; b = x;
        } else if (hue >= 180 && hue < 240) {
            r = 0; g = x; b = c;
        } else if (hue >= 240 && hue < 300) {
            r = x; g = 0; b = c;
        } else if (hue >= 300 && hue < 360) {
            r = c; g = 0; b = x;
        }

        const red = Math.round((r + m) * 255);
        const green = Math.round((g + m) * 255);
        const blue = Math.round((b + m) * 255);

        return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue
            .toString(16)
            .padStart(2, '0')}`;
    }
}
